// src/redux/slices/auth.slice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { AuthService } from '../../api';
import { LoginDto } from '../../api/models/LoginDto';
import { ApiError } from '../../api/core/ApiError';
import { PermissionString } from '../../types/permission.types';
import { UsersServiceExtendedHelpers } from '../../utils/api-helpers/UsersServiceExtended.helpers';
import {
  isMustChangePasswordError,
  handleMustChangePasswordError,
  resetAuthErrorHandlingState,
} from '../../utils/authErrorHandler';
import { clearActiveTabsSession } from '../../utils/dashboardTabsSession';

// Types
export type AuthRole =
  | string
  | {
      id?: string;
      name?: string;
      description?: string;
    };

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: AuthRole[];
  permissions?: PermissionString[];
  primaryLocationId?: number | null;
  canSellFromOtherLocations?: boolean;
}

/** Normalize login/profile role shapes (object or legacy string). */
export function getAuthRoleName(role: AuthRole | null | undefined): string | undefined {
  if (role == null) return undefined;
  if (typeof role === 'string') return role;
  return role.name;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: PermissionString[];
  /** True after the first fetchProfile attempt settles (avoids false "no access" on reload). */
  permissionsLoaded: boolean;
  mustChangePassword: boolean;
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginDto, { rejectWithValue }) => {
    try {
      const response = await AuthService.authControllerLogin(credentials);
      resetAuthErrorHandlingState();
      return {
        access_token: response.access_token,
        user: response.user,
        mustChangePassword: response.mustChangePassword ?? false,
      };
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        const serverMessage = error.body?.message ?? error.message ?? 'Login failed';
        return rejectWithValue(Array.isArray(serverMessage) ? serverMessage[0] : serverMessage);
      }
      return rejectWithValue('Error de red. Intente de nuevo.');
    }
  },
);

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await AuthService.authControllerGetProfile();

      // Fetch user permissions
      let permissions: PermissionString[] = [];
      try {
        permissions = await UsersServiceExtendedHelpers.getMyPermissions();
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
        // Log the full error for debugging
        if (error instanceof ApiError) {
          console.error('Permission API Error:', {
            status: error.status,
            message: error.message,
            body: error.body,
          });
        }
        // Continue without permissions - they can be loaded later
      }

      return {
        ...response,
        permissions,
      };
    } catch (error: unknown) {
      if (isMustChangePasswordError(error)) {
        handleMustChangePasswordError();
        return rejectWithValue('Must change password');
      }
      if (error instanceof ApiError) {
        if (error.status === 401) {
          return rejectWithValue('Session expired');
        }
        return rejectWithValue(error.message || 'Failed to fetch profile');
      }
      return rejectWithValue('Network error. Please try again.');
    }
  },
);

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  permissions: [],
  permissionsLoaded: false,
  mustChangePassword: false,
};

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.permissions = [];
      state.permissionsLoaded = false;
      state.mustChangePassword = false;
      // Workspace tabs must not survive a new session (manual logout, 401, etc.).
      clearActiveTabsSession();
    },
    clearError: (state) => {
      state.error = null;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
      state.permissionsLoaded = true;
      if (state.user) {
        state.user.permissions = action.payload;
      }
    },
    setMustChangePassword: (state, action: { payload: boolean }) => {
      state.mustChangePassword = action.payload;
    },
  },
  extraReducers: (builder) => {
    // After persist rehydrate: persisted permissions are usable immediately (avoids
    // blank/deny flash). fetchProfile still revalidates and may replace them.
    // persistReducer(key: 'auth') delivers payload as AuthState, not { auth: ... }.
    builder.addCase(REHYDRATE, (state, action) => {
      const rehydrateAction = action as {
        key?: string;
        payload?: Partial<AuthState> | null;
      };
      if (rehydrateAction.key != null && rehydrateAction.key !== 'auth') {
        return;
      }
      const inbound = rehydrateAction.payload;
      if (inbound && Array.isArray(inbound.permissions) && inbound.permissions.length > 0) {
        state.permissionsLoaded = true;
      }
    });

    // Login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.access_token;
      state.mustChangePassword = action.payload.mustChangePassword;
      state.permissions = [];
      state.permissionsLoaded = false;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch Profile
    builder.addCase(fetchProfile.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = {
        ...action.payload,
        permissions: action.payload.permissions ?? [],
      };
      state.permissions = action.payload.permissions ?? [];
      state.permissionsLoaded = true;
      state.isAuthenticated = true;
    });
    builder.addCase(fetchProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.permissionsLoaded = true;
      if (action.payload === 'Session expired') {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.permissionsLoaded = false;
      }
      if (action.payload === 'Must change password') {
        state.mustChangePassword = true;
      }
    });
  },
});

// Export actions and reducer
export const { logout, clearError, setPermissions, setMustChangePassword } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectUserPermissions = (state: { auth: AuthState }) => state.auth.permissions;
export const selectPermissionsLoaded = (state: { auth: AuthState }) => state.auth.permissionsLoaded;
export const selectHasRole = (role: string) => (state: { auth: AuthState }) =>
  state.auth.user?.roles.some((r) => getAuthRoleName(r) === role) ?? false;
export const selectCanPermission = (permission: PermissionString) => (state: { auth: AuthState }) =>
  state.auth.permissions.includes(permission);
export const selectMustChangePassword = (state: { auth: AuthState }) =>
  state.auth.mustChangePassword;
